import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Users, FileText, Phone, IdCard, Plus, Pencil, Trash2, FileSpreadsheet, Cake } from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Pagination } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatTanggal } from "@/lib/format";
import { toast } from "sonner";

const LEASE_LABEL = { active: "Sewa Aktif", awaiting_movein: "Menunggu Masuk", ended: "Selesai", early_exit: "Keluar Awal", scheduled: "Perpanjangan", tanpa_sewa: "Tanpa Sewa" };

function ageFrom(dob) {
  if (!dob) return null;
  const d = new Date(String(dob).slice(0, 10) + "T00:00:00");
  if (isNaN(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 150 ? age : null;
}

export default function Penghuni() {
  const { projects, projectId, projectParam } = useProjects();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["residents", projectId, q, page],
    queryFn: async () => (await api.get("/residents", { params: { ...projectParam, q: q || undefined, page, page_size: 15 } })).data,
  });

  const deleteResident = async (r) => {
    if (!window.confirm(`Hapus penghuni "${r.name}"? Hanya dapat dihapus jika tanpa riwayat sewa/booking.`)) return;
    try { await api.delete(`/residents/${r.id}`); toast.success("Penghuni dihapus."); qc.invalidateQueries({ queryKey: ["residents"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const exportResidents = async (fmt) => {
    try {
      const res = await api.get(`/residents/export.${fmt}`, {
        params: { ...projectParam, q: q || undefined },
        responseType: "blob",
      });
      downloadBlob(res.data, `daftar-penghuni.${fmt}`);
    } catch { toast.error("Gagal mengekspor daftar penghuni."); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Penghuni" description="Data penghuni dan dokumen identitas (akses terbatas sesuai proyek)."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportResidents("csv")} data-testid="resident-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => exportResidents("xlsx")} data-testid="resident-export-xlsx"><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="add-resident-button"><Plus className="mr-2 h-4 w-4" /> Tambah Penghuni</Button>
        </div>} />

      <SectionCard className="!p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari nama atau nomor WhatsApp..." className="h-9 pl-9" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="resident-search" />
        </div>
      </SectionCard>

      <SectionCard className="!p-0 overflow-hidden">
        {isLoading ? <Loading /> : !data?.items?.length ? (
          <EmptyState title="Belum ada penghuni" icon={Users} />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nama</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.id} data-testid={`resident-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.whatsapp}</TableCell>
                    <TableCell><span className="text-xs">{LEASE_LABEL[r.current_status] || r.current_status}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setDetailId(r.id)} data-testid={`resident-detail-${r.id}`}>Detail</Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditId(r.id)} data-testid={`resident-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => deleteResident(r)} data-testid={`resident-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-4"><Pagination page={page} total={data.total} pageSize={data.page_size} onPage={setPage} /></div>
          </>
        )}
      </SectionCard>

      {detailId && <ResidentDetail id={detailId} onClose={() => setDetailId(null)} />}
      {createOpen && <CreateResident projects={projects} defaultProject={projectId}
        onClose={() => setCreateOpen(false)}
        onDone={() => { setCreateOpen(false); qc.invalidateQueries({ queryKey: ["residents"] }); }} />}
      {editId && <EditResident id={editId} onClose={() => setEditId(null)}
        onDone={() => { setEditId(null); qc.invalidateQueries(); }} />}
    </div>
  );
}

function ResidentFields({ f, setF, showNik = true }) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div><Label>Nama Panggilan</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="resident-field-name" /></div>
        <div><Label>WhatsApp Penghuni</Label><Input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} data-testid="resident-field-whatsapp" /></div>
        <div><Label>Tanggal Lahir</Label><Input type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="resident-field-dob" /></div>
      </div>
      <div className="rounded-lg border border-dashed border-border p-3 space-y-3">
        <div className="text-xs font-medium text-muted-foreground">Identitas (opsional)</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label className="text-xs">Nama sesuai KTP</Label><Input value={f.full_name_ktp} onChange={(e) => setF({ ...f, full_name_ktp: e.target.value })} data-testid="resident-field-ktpname" /></div>
          {showNik && <div><Label className="text-xs">NIK (16 digit)</Label><Input value={f.nik} onChange={(e) => setF({ ...f, nik: e.target.value })} inputMode="numeric" maxLength={16} data-testid="resident-field-nik" /></div>}
        </div>
        <div><Label className="text-xs">Alamat sesuai KTP</Label><Textarea value={f.address_ktp} onChange={(e) => setF({ ...f, address_ktp: e.target.value })} data-testid="resident-field-address" /></div>
        <div><Label className="text-xs">WhatsApp Wali</Label><Input value={f.guardian_whatsapp} onChange={(e) => setF({ ...f, guardian_whatsapp: e.target.value })} data-testid="resident-field-guardian" /></div>
      </div>
    </>
  );
}

function CreateResident({ projects, defaultProject, onClose, onDone }) {
  const [f, setF] = useState({
    project_id: defaultProject && defaultProject !== "all" ? defaultProject : "",
    name: "", whatsapp: "", dob: "", full_name_ktp: "", nik: "", address_ktp: "", guardian_whatsapp: "",
  });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.project_id) { toast.error("Pilih proyek."); return; }
    if (!f.name.trim() || !f.whatsapp.trim()) { toast.error("Nama & WhatsApp wajib diisi."); return; }
    if (f.nik && !/^\d{16}$/.test(f.nik.trim())) { toast.error("NIK harus 16 digit angka."); return; }
    setLoading(true);
    try { await api.post("/residents", f); toast.success("Penghuni ditambahkan."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resident-create-dialog">
        <DialogHeader><DialogTitle>Tambah Penghuni</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Proyek</Label>
            <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v })}>
              <SelectTrigger data-testid="resident-field-project"><SelectValue placeholder="Pilih proyek" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ResidentFields f={f} setF={setF} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="resident-create-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditResident({ id, onClose, onDone }) {
  const { data, isLoading } = useQuery({ queryKey: ["resident", id], queryFn: async () => (await api.get(`/residents/${id}`)).data });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="resident-edit-dialog">
        <DialogHeader><DialogTitle>Ubah Penghuni</DialogTitle></DialogHeader>
        {isLoading || !data?.resident ? <Loading /> : <EditResidentForm r={data.resident} onClose={onClose} onDone={onDone} />}
      </DialogContent>
    </Dialog>
  );
}

function EditResidentForm({ r, onClose, onDone }) {
  const [f, setF] = useState({
    name: r.name || "", whatsapp: r.whatsapp || "", dob: r.dob || "", full_name_ktp: r.full_name_ktp || "",
    nik: r.nik || "", address_ktp: r.address_ktp || "", guardian_whatsapp: r.guardian_whatsapp || "",
  });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.whatsapp.trim()) { toast.error("Nama & WhatsApp wajib diisi."); return; }
    if (f.nik && !/^\d{16}$/.test(f.nik.trim())) { toast.error("NIK harus 16 digit angka."); return; }
    setLoading(true);
    try { await api.patch(`/residents/${r.id}`, f); toast.success("Penghuni diperbarui."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <>
      <div className="space-y-3"><ResidentFields f={f} setF={setF} /></div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="resident-edit-save">Simpan</Button></DialogFooter>
    </>
  );
}

function ResidentDetail({ id, onClose }) {
  const { data, isLoading } = useQuery({ queryKey: ["resident", id], queryFn: async () => (await api.get(`/residents/${id}`)).data });
  const [ktpLoading, setKtpLoading] = useState(false);
  const r = data?.resident;

  const viewKtp = async () => {
    setKtpLoading(true);
    try {
      const res = await api.get(`/residents/${id}/ktp`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { /* no doc */ } finally { setKtpLoading(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resident-detail-dialog">
        {isLoading || !r ? (
          <>
            <DialogTitle className="sr-only">Memuat detail penghuni</DialogTitle>
            <Loading />
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>{r.name}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-sm">
                <Field icon={Phone} label="WhatsApp Penghuni" value={r.whatsapp} />
                <Field icon={Phone} label="WhatsApp Wali" value={r.guardian_whatsapp} />
                <Field icon={IdCard} label="Nama sesuai KTP" value={r.full_name_ktp} />
                <Field icon={IdCard} label="NIK" value={r.nik} />
                <Field icon={Cake} label="Tanggal Lahir" value={r.dob ? `${formatTanggal(r.dob)}${ageFrom(r.dob) != null ? ` · ${ageFrom(r.dob)} th` : ""}` : "-"} />
                <div className="sm:col-span-2"><Field label="Alamat sesuai KTP" value={r.address_ktp} /></div>
              </div>
              <Button variant="outline" onClick={viewKtp} disabled={ktpLoading || !r.has_identity} data-testid="view-ktp-button">
                <FileText className="mr-2 h-4 w-4" /> {r.has_identity ? "Lihat Dokumen KTP" : "KTP belum diunggah"}
              </Button>

              <div>
                <div className="mb-2 text-sm font-semibold">Riwayat Sewa</div>
                {data.leases?.length ? (
                  <div className="space-y-1.5">
                    {data.leases.map((l) => (
                      <div key={l.id} className="flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2 text-xs">
                        <span>Kamar {l.room_number} · {l.period_label}</span>
                        <StatusBadge kind="lease" value={l.status} />
                      </div>
                    ))}
                  </div>
                ) : <div className="text-xs text-muted-foreground">Belum ada riwayat sewa.</div>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className="mt-0.5 font-medium">{value || "-"}</div>
    </div>
  );
}
